<template>
  <div class="flex flex-column relative">
    <div v-if="useScanMode || useGalleryMode || useSignatureMode" style="display: flex; flex-direction: column; gap: .5rem; margin-top: 1rem; margin-bottom: -.5rem;">
      <div style="display: flex; gap: .5rem; align-items: center;">
        <Button v-if="useScanMode" @click="openScanModal" type="button" severity="warning" :disabled="disabled">
          <div class="flex align-items-center justify-content-center w-full gap-1">
            <i class="fa fa-print" aria-hidden="true"></i>
            {{ $t('Scan') }}
          </div>
        </Button>
        <Button v-if="useGalleryMode" @click="openGalleryModal" type="button" severity="success" :disabled="disabled">
          <div class="flex align-items-center justify-content-center w-full gap-1">
            <i class="fa fa-folder-open" aria-hidden="true"></i>
            {{ $t('Gallery') }}
          </div>
        </Button>
        <div v-if="useSignatureMode" class="flex-1">
          <Button @click="signUploadedFiles = !signUploadedFiles" :severity="signUploadedFiles ? 'primary' : 'secondary'" :label="$t('Digitally sign documents')" :icon="signUploadedFiles ? 'fa fa-check-square' : 'fa fa-square'"/>
        </div>
      </div>
      <div style="border: 1px solid var(--surface-50); border-radius: var(--border-radius); padding: .5rem;">
        <span>{{$t('To scan/pick/digitally sign documents, you have to install:')}}</span><a href="/page/676" target="_blank" class="ml-1">PDF signer</a>
      </div>
    </div>

    <label
      v-if="(isMultiple || !modelValue) && !canHideInput"
      class="upload-control"
      :class="{ active: isDragOver, 'p-error': invalid, 'p-disabled': disabled }"
      @drop.stop.prevent="onDrop"
      @dragend.stop.prevent="onDrag($event, false)"
      @dragenter.stop.prevent="onDrag($event, true)"
      @drag.stop.prevent="onDrag"
      @dragleave.stop.prevent="onDrag($event, false)"
      @dragover.stop.prevent="onDrag($event, true)"
      @dragstart.stop.prevent="onDrag($event, true)"
    >
      <div v-if="!fileName">
        <span class="flex align-items-center justify-content-center"
          ><i class="fa fa-upload" aria-hidden="true"></i> {{ label }}</span
        >
        <small class="block"
          ><em>{{ $t('Click here or drag files.') }}</em></small
        >
      </div>

      <div v-else-if="fileName">
        <span class="flex align-items-center justify-content-center">{{ getFileName(fileName) }}</span>
      </div>

      <input
        type="file"
        @change="onFileChange"
        :multiple="isMultiple"
        style="display: none"
        :accept="acceptedMimeTypes"
        ref="fileUpload"
        @click="capacitorUpload"
      />
      <template v-if="error && showErrors">
        <small v-if="typeof error === 'string'" class="p-error">{{ error }}</small>
        <template v-else-if="typeof error === 'object' && error.length">
          <template v-for="errorObj in error">
            <small class="p-error">{{ errorObj.$message }}</small>
          </template>
        </template>
      </template>
    </label>

    <label v-else>{{ label }}</label>

    <div v-if="modelValue && preview" class="my-2 grid overflow-y-auto" style="max-height: 50vh">
      <template v-if="isMultiple && Array.isArray(modelValue)">
        <div v-for="file in modelValue" :class="filesGridClasses">
          <FileCard :showRemove="!disabled" :file-url="file" @remove="removeFile" />
        </div>
      </template>
      <div v-else class="col-12">
        <FileCard :showRemove="!disabled" :file-url="modelValue" @remove="removeFile" />
      </div>
    </div>
    <Loader :is-loading="isLoading" :is-local-loader="true" />

    <Dialog
      v-model:visible="showScanModal"
      :header="$t('Scan Document')"
      :modal="true"
      :style="{ minWidth: '50vw' }"
      :dismissableMask="true"
    >
      <ScannerUpload
        v-if="showScanModal"
        :simulate-hub="false"
        @filesSelected="handleFilesSelected"
        @closeDialog="closeScanModal"
      />
    </Dialog>

    <Dialog
      v-model:visible="showGalleryModal"
      :header="$t('Pick files')"
      :modal="true"
      :style="{ maxWidth: '100vw', width: '50rem' }"
      :dismissableMask="true"
    >
      <GalleryUpload
        v-if="showGalleryModal"
        @closeDialog="closeGalleryModal"
        @filesSelected="handleFilesSelected"
      />
    </Dialog>
  </div>
</template>

<script>
import CryptoJS from 'crypto-js';
import Loading from 'vue3-loading-overlay';
import FileCard from '@/components/cards/FileCard.vue';
import { isJsonString } from '@/js/utils';
import { mapFormDataBySchema } from '@/js/document/document';
import { Camera, CameraResultType } from '@capacitor/camera';
import Loader from '@/components/common/Loader.vue';
import ScannerUpload from '@/components/inputs/ScannerUpload.vue';
import GalleryUpload from '@/components/inputs/GalleryUpload.vue';
import Dialog from 'primevue/dialog';

export default {
  name: 'MultiUpload',
  components: {
    Loader,
    FileCard,
    Loading,
    ScannerUpload,
    GalleryUpload,
    Dialog,
  },
  props: {
    modelValue: String,
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMultiple: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: true,
    },
    maxFiles: {
      type: Number,
      default: 0,
    },
    maxFileSize: {
      type: Number,
      default: 0, // in MegaBytes
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
    modelType: {
      type: String,
      default: 'url', // url or object
    },
    modelSchema: {
      type: String,
      required: false,
    },
    saveToLocalStorage: {
      type: Boolean,
      default: false,
    },
    acceptedMimeTypes: {
      type: String,
      default: '*',
    },
    rejectedMimeTypes: {
      type: String,
      default: null,
    },
    executeUpload: {
      type: Boolean,
      default: true,
    },
    convertTo: {
      type: String,
    },
    isLayerMarker: {
      type: Boolean,
      default: false,
    },
    onlySignedFile: {
      type: Boolean,
      default: false,
    },
    filesGridClasses: {
      type: String,
      default: 'grid-column col-6 sm:col-6 md:col-4 lg:col-3 xl:col-3 p-1',
    },
    canHideInput: {
      type: Boolean,
      default: false,
    },
    useScanMode: {
      type: Boolean,
      default: false,
    },
    useGalleryMode: {
      type: Boolean,
      default: false,
    },
    useSignatureMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'fileSelect', 'removeFile'],
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    useCapacitorUpload() {
      return this.$deviceInfo?.platform === 'android' && /image/.test(this.acceptedMimeTypes);
    },
  },
  data() {
    return {
      isLoading: false,
      isDragOver: false,
      fileName: undefined,
      showScanModal: false,
      showGalleryModal: false,
      signUploadedFiles: false,
      thumbprint: null,
    };
  },
  mounted() {
    if (this.useCapacitorUpload) {
      this.$refs.fileUpload.type = 'text'; // avoid file upload
    }
  },
  watch: {
    async signUploadedFiles() {
      if (this.signUploadedFiles) {
        this.thumbprint = await this.$certificatePicker.pickCertificate();
        if (!this.thumbprint) {
          this.signUploadedFiles = false;
          return;
        }
      }
    },
  },
  methods: {
    async capacitorUpload() {
      if (this.useCapacitorUpload) {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
        });
        let blob = await fetch(image.webPath).then((r) => r.blob());
        const file = new File([blob], 'image.jpg');
        if (this.executeUpload) {
          await this.triggerUpload([file]);
        } else {
          this.fileName = image.webPath;
        }
        this.$emit('fileSelect', blob);
      }
    },
    onFileChange(e) {
      if (this.useCapacitorUpload) {
        return;
      }
      const input = e.target;
      const files = Array.from(e.target.files || e.dataTransfer.files || e);
      const rejected = (this.rejectedMimeTypes || '')
        .split(',')
        .map((type) => type.trim())
        .filter((type) => type.length > 0);
      const validFiles = files.filter((file) => !rejected.includes(file.type));
      const invalidFiles = files.filter((file) => rejected.includes(file.type));
      if (invalidFiles.length) {
        console.warn('Rejected files:', invalidFiles);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `${this.$t('Rejected files')}:\n${invalidFiles.map((invF) => invF.name + '\n')}`,
          life: 5000,
        });
        if (validFiles.length === 0 && input) {
          input.value = '';
          return;
        }
      }
      if (validFiles.length) {
        if (this.executeUpload) {
          this.triggerUpload(validFiles);
        } else {
          this.fileName = validFiles[0].name;
        }
        this.$emit('fileSelect', validFiles[0]);
      }
    },
    async triggerUpload(files) {
      if (this.useSignatureMode && this.signUploadedFiles) {
        files = await this.digitallySignFiles(files);
      }

      let uploaded = 0;
      for (let file of files) {
        if (
          (!this.maxFileSize ||
            (this.maxFileSize > 0 && parseInt(file?.size || 0) <= this.maxFileSize * 1024 * 1024)) &&
          (!this.maxFiles || uploaded < this.maxFiles)
        ) {
          let theFile = new FormData();
          theFile.append('file', file.file || file);
          theFile.append('saveToLocalStorage', this.saveToLocalStorage);
          theFile.append('isMarker', this.isLayerMarker);
          if (this.document?.documentId) {
            theFile.append('documentId', this.document.documentId);
          }
          if (this.convertTo) theFile.append('convertTo', this.convertTo);
          this.uploadFile(theFile);
          uploaded += 1;
        }
      }
      if (uploaded < files.length) {
        let message = `Not all files are uploaded.`;
        if (this.maxFileSize) {
          message += ` Max ${this.maxFileSize}MB/file are allowed.`;
        }
        if (this.maxFiles) {
          message += ` Max ${this.maxFiles} files are allowed.`;
        }
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: message,
          life: 5000,
        });
      }
    },
    uploadFile(file) {
      this.isLoading = true;
      let params = { isMarker: this.isLayerMarker };
      if (this.document?.documentId) params.documentId = this.document.documentId;
      this.$store
        .dispatch(this.onlySignedFile ? 'uploadSignedFile' : 'uploadFile', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
          },
          data: {
            file,
            params: params,
          },
        })
        .then((response) => {
          let uploadValue;
          if (this.modelType === 'url') {
            uploadValue = response.url;
          } else {
            if (!this.modelSchema) {
              uploadValue = response;
            } else {
              let schema = isJsonString(this.modelSchema) ? JSON.parse(this.modelSchema) : this.modelSchema;
              if (
                typeof schema === 'object' &&
                ((Array.isArray(schema) && schema.length) || Object.keys(schema).length)
              ) {
                uploadValue = mapFormDataBySchema(schema, response);
              } else {
                uploadValue = response;
              }
            }
          }

          if (this.isMultiple) {
            if (!this.value || !Array.isArray(this.value)) {
              this.value = [];
            }
            /**
             * Fixes a bug where some multi-uploads contain the same item twice.
             */
            // else if (this.value && !Array.isArray(this.value)) {
            //   this.value = [this.value];
            // }

            setTimeout(() => {
              this.value.push(uploadValue);
            }, 200);
          } else {
            this.value = uploadValue;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: this.$t(error),
            life: 5000,
          });
        });
    },
    getFileName(fileName) {
      if (fileName) {
        return fileName.split('/').pop();
      } else return this.$t('View file');
    },
    removeFile(fileUrl) {
      if (this.isMultiple && Array.isArray(this.value)) {
        this.value = this.value.filter((f) => f !== fileUrl);
      } else {
        this.value = null;
      }
      this.fileName = undefined;
      this.$emit('removeFile', fileUrl, this.value);
    },
    onDrop(e) {
      const files = e.dataTransfer.files || e?.target?.files;
      if (files.length) {
        if (this.executeUpload) {
          this.triggerUpload(files);
        } else {
          this.fileName = files[0].name;
        }
        this.$emit('fileSelect', files[0]);
      }
      this.isDragOver = false;
    },
    onDrag(e, value) {
      this.isDragOver = value;
    },
    openScanModal() {
      this.showScanModal = true;
    },
    openGalleryModal() {
      this.showGalleryModal = true;
    },
    closeScanModal() {
      this.showScanModal = false;
    },
    closeGalleryModal() {
      this.showGalleryModal = false;
    },
    handleFilesSelected(files) {
      console.log('Files selected:', files);
      if (this.executeUpload) {
        this.triggerUpload(files);
      } else {
        this.fileName = files[0].name;
      }
      this.$emit('fileSelect', files[0]);
      this.closeScanModal();
    },
    async digitallySignFiles(files) {
      const signedFiles = [];

      for (const file of files) {
        const originalFile = file.file || file;
        const originalFileName = originalFile.name || 'file';
        const originalFileType = originalFile.type || 'application/octet-stream';

        let fileBase64 = originalFile;
        // Check if file is a valid Blob/File object
        if (originalFile instanceof Blob || originalFile instanceof File) {
          // Convert file to base64
          fileBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(originalFile);
          });
        }

        if (!this.thumbprint) this.thumbprint = await this.$certificatePicker.pickCertificate();
        if (!this.thumbprint) {
          this.signUploadedFiles = false;
          return;
        }
        const usbSignPdf = await this.$store.dispatch('usbSignPDF', {
          data: {
            pdfBase64: fileBase64,
            thumbprint: this.thumbprint,
            options: {
              appearances: [],
            },
          },
        });

        const byteCharacters = atob(usbSignPdf.pdfFileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: originalFileType });

        const signedFile = new File([blob], originalFileName, { type: originalFileType });

        signedFiles.push(signedFile);
      }
      console.log('signedFiles', signedFiles);
      return signedFiles;
    },
  },
};
</script>

<style lang="scss">
.upload-control {
  display: block;
  cursor: pointer;
  border: 3px dashed #ccc;
  border-radius: 10px;
  padding: 30px 10px;
  background: #f0f0f4;
  margin-top: 20px;
  font-size: 20px;
  text-align: center;

  small {
    font-size: 12px;
    margin-top: 5px;
  }

  i {
    margin-right: 10px;
  }

  &:hover,
  &:focus,
  &.active {
    border: 3px dashed #0f8bfd;
  }
}

html.dark {
  .upload-control {
    background: transparent;
  }
}
</style>
